import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Step } from 'react-joyride';
import { useTourStore } from '../stores/tourStore';
import { notify } from '../lib/notify';

export interface ExtendedStep extends Step {
  navigateTo?: string; // Optional navigation path after this step
}

export interface TourConfig {
  id: string;
  steps: ExtendedStep[];
  continuous?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  /** First-run tour auto-launched for new users; shows a "don't show again" control. */
  firstRun?: boolean;
}

/**
 * Dashboard Tour Configuration
 * This tour guides users through the main dashboard features
 */
export const dashboardTour: TourConfig = {
  id: 'dashboard-tour',
  continuous: true,
  showProgress: true,
  showSkipButton: true,
  steps: [
    {
      target: 'body',
      content: 'onboarding.dashboard.welcome',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="kpi-cards"]',
      content: 'onboarding.dashboard.kpiCards',
      placement: 'bottom',
    },
    {
      target: '[data-tour="kpi-domains"]',
      content: 'onboarding.dashboard.kpiDomains',
      placement: 'bottom',
    },
    {
      target: '[data-tour="kpi-usecases"]',
      content: 'onboarding.dashboard.kpiUseCases',
      placement: 'bottom',
    },
    {
      target: '[data-tour="kpi-cadrages"]',
      content: 'onboarding.dashboard.kpiCadrages',
      placement: 'bottom',
    },
    {
      target: '[data-tour="quick-actions"]',
      content: 'onboarding.dashboard.quickActions',
      placement: 'bottom',
    },
    {
      target: '[data-tour="recent-domains"]',
      content: 'onboarding.dashboard.recentDomains',
      placement: 'top',
    },
    {
      target: '[data-tour="cadrage-status"]',
      content: 'onboarding.dashboard.cadrageStatus',
      placement: 'top',
    },
    {
      target: '[data-tour="use-case-matrix"]',
      content: 'onboarding.dashboard.useCaseMatrix',
      placement: 'top',
    },
    {
      target: '[data-tour="charts-section"]',
      content: 'onboarding.dashboard.charts',
      placement: 'top',
    },
    {
      target: '[data-tour="activity-feed"]',
      content: 'onboarding.dashboard.activityFeed',
      placement: 'left',
    },
    {
      target: '[data-tour="org-members"]',
      content: 'onboarding.dashboard.orgMembers',
      placement: 'left',
    },
    {
      target: '[data-tour="genie-importer"]',
      content: 'onboarding.dashboard.genieImporter',
      placement: 'bottom',
    },
    {
      target: '[data-tour="sidebar"]',
      content: 'onboarding.dashboard.sidebar',
      placement: 'right',
    },
    {
      target: 'body',
      content: 'onboarding.dashboard.complete',
      placement: 'center',
    },
  ],
};

/**
 * User Journey Tour Configuration
 * This tour guides users through the complete workflow from domain to cadrage
 */
export const userJourneyTour: TourConfig = {
  id: 'user-journey-tour',
  continuous: true,
  showProgress: true,
  showSkipButton: true,
  steps: [
    // Introduction
    {
      target: 'body',
      content: 'onboarding.userJourney.welcome',
      placement: 'center',
      disableBeacon: true,
    },
    
    // PHASE 1: Domain Creation
    {
      target: '[data-tour="domain-name-field"]',
      content: 'onboarding.userJourney.domainName',
      placement: 'bottom',
    },
    {
      target: '[data-tour="domain-description-field"]',
      content: 'onboarding.userJourney.domainDescription',
      placement: 'bottom',
    },
    {
      target: '[data-tour="domain-forge-selector"]',
      content: 'onboarding.userJourney.domainForge',
      placement: 'bottom',
    },
    {
      target: '[data-tour="domain-submit-btn"]',
      content: 'onboarding.userJourney.domainSubmit',
      placement: 'top',
    },
    
    // Transition to Use Case
    {
      target: 'body',
      title: 'onboarding.userJourney.transitionToUseCaseTitle',
      content: 'onboarding.userJourney.transitionToUseCase',
      placement: 'center',
      navigateTo: '/cas-usage/new', // Navigate to use case creation
    },
    
    // PHASE 2: Use Case Creation
    {
      target: '[data-tour="usecase-domain-selector"]',
      content: 'onboarding.userJourney.useCaseDomain',
      placement: 'bottom',
    },
    {
      target: '[data-tour="usecase-name-field"]',
      content: 'onboarding.userJourney.useCaseName',
      placement: 'bottom',
    },
    {
      target: '[data-tour="usecase-description-field"]',
      content: 'onboarding.userJourney.useCaseDescription',
      placement: 'bottom',
    },
    {
      target: '[data-tour="usecase-business-need"]',
      content: 'onboarding.userJourney.useCaseBusinessNeed',
      placement: 'bottom',
    },
    {
      target: '[data-tour="usecase-submit-btn"]',
      content: 'onboarding.userJourney.useCaseSubmit',
      placement: 'top',
    },
    
    // Transition to Cadrage
    {
      target: 'body',
      title: 'onboarding.userJourney.transitionToCadrageTitle',
      content: 'onboarding.userJourney.transitionToCadrage',
      placement: 'center',
      navigateTo: '/cadrages/new', // Navigate to cadrage creation
    },
    
    // PHASE 3: Cadrage Creation
    {
      target: '[data-tour="cadrage-usecase-selector"]',
      content: 'onboarding.userJourney.cadrageUseCase',
      placement: 'bottom',
    },
    {
      target: '[data-tour="cadrage-name-field"]',
      content: 'onboarding.userJourney.cadrageName',
      placement: 'bottom',
    },
    {
      target: '[data-tour="cadrage-client-field"]',
      content: 'onboarding.userJourney.cadrageClient',
      placement: 'bottom',
    },
    {
      target: '[data-tour="cadrage-company-info"]',
      content: 'onboarding.userJourney.cadrageCompanyInfo',
      placement: 'bottom',
    },
    {
      target: '[data-tour="cadrage-submit-btn"]',
      content: 'onboarding.userJourney.cadrageSubmit',
      placement: 'top',
    },
    
    // Completion
    {
      target: 'body',
      content: 'onboarding.userJourney.complete',
      placement: 'center',
    },
  ],
};

// Derived from the steps so pages and the tour guard stay in sync when steps change.
export const USER_JOURNEY_TO_CADRAGE_STEP = userJourneyTour.steps.findIndex(
  (step) => step.navigateTo === '/cadrages/new'
);
export const USER_JOURNEY_COMPLETE_STEP = userJourneyTour.steps.length - 1;

/**
 * Genie Importer Interactive Tour Configuration
 * This tour guides users through the AI document extraction workflow
 */
export const genieImporterTour: TourConfig = {
  id: 'genie-importer-tour',
  continuous: true, // Continuous mode with instructional content
  showProgress: true,
  showSkipButton: true,
  steps: [
    // Introduction
    {
      target: 'body',
      content: 'onboarding.genieImporter.welcome',
      placement: 'center',
      disableBeacon: true,
    },
    
    // Step 1: Show the textarea/upload area
    {
      target: '[data-tour="genie-content-input"]',
      content: 'onboarding.genieImporter.contentInput',
      placement: 'bottom',
    },
    
    // Step 2: Show the import button
    {
      target: '[data-tour="genie-import-button"]',
      content: 'onboarding.genieImporter.importButton',
      placement: 'left',
    },
    
    // Step 3: Domain selection
    {
      target: '[data-tour="genie-domain-selector"]',
      content: 'onboarding.genieImporter.domainSelector',
      placement: 'bottom',
    },
    
    // Step 4: Action selection
    {
      target: '[data-tour="genie-action-selector"]',
      content: 'onboarding.genieImporter.actionSelector',
      placement: 'bottom',
    },
    
    // Step 5: Launch button
    {
      target: '[data-tour="genie-launch-button"]',
      content: 'onboarding.genieImporter.launchButton',
      placement: 'top',
    },
    
    // Step 6: Job list appears
    {
      target: '[data-tour="genie-job-list"]',
      content: 'onboarding.genieImporter.jobList',
      placement: 'top',
    },

    // Step 7: Job detail view
    {
      target: '[data-tour="genie-job-detail"]',
      content: 'onboarding.genieImporter.jobDetail',
      placement: 'top',
    },
    
    // Step 10: Approve/Reject buttons
    {
      target: '[data-tour="genie-job-actions"]',
      content: 'onboarding.genieImporter.jobActions',
      placement: 'top',
    },
    
    // Step 11: Final result explanation
    {
      target: 'body',
      content: 'onboarding.genieImporter.complete',
      placement: 'center',
    },
  ],
};

/**
 * First-Run Welcome Tour
 * A short, cross-feature overview auto-launched for new users on first login.
 * Anchored only to always-present chrome (sidebar + header) so it runs on any page
 * without forcing navigation. Includes a "don't show again" control.
 */
export const welcomeTour: TourConfig = {
  id: 'welcome',
  continuous: true,
  showProgress: true,
  showSkipButton: true,
  firstRun: true,
  steps: [
    {
      target: 'body',
      content: 'onboarding.welcome.intro',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-welcome"]',
      title: 'onboarding.welcome.genieArtifactTitle',
      content: 'onboarding.welcome.genieArtifact',
      placement: 'right',
    },
    {
      target: '[data-tour="nav-dashboard"]',
      content: 'onboarding.welcome.dashboard',
      placement: 'right',
    },
    {
      target: '[data-tour="sidebar-business"]',
      content: 'onboarding.welcome.business',
      placement: 'right',
    },
    {
      target: '[data-tour="sidebar-knowledge"]',
      content: 'onboarding.welcome.knowledge',
      placement: 'right',
    },
    {
      target: '[data-tour="help-menu"]',
      content: 'onboarding.welcome.help',
      placement: 'bottom',
    },
    {
      target: 'body',
      content: 'onboarding.welcome.complete',
      placement: 'center',
    },
  ],
};

/**
 * GenieArtifact Tour
 * Walks through the AI document/assistant entry point on the Welcome page.
 */
export const genieArtifactTour: TourConfig = {
  id: 'genieArtifact',
  continuous: true,
  showProgress: true,
  showSkipButton: true,
  steps: [
    {
      target: 'body',
      content: 'onboarding.genieArtifact.welcome',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="genie-artifact-input"]',
      content: 'onboarding.genieArtifact.input',
      placement: 'bottom',
    },
    {
      target: '[data-tour="genie-artifact-attach"]',
      content: 'onboarding.genieArtifact.attach',
      placement: 'top',
    },
    {
      target: '[data-tour="genie-artifact-starters"]',
      content: 'onboarding.genieArtifact.starters',
      placement: 'top',
    },
    {
      target: '[data-tour="genie-artifact-info"]',
      content: 'onboarding.genieArtifact.capabilities',
      placement: 'bottom',
    },
    {
      target: '[data-tour="genie-artifact-history"]',
      content: 'onboarding.genieArtifact.history',
      placement: 'bottom',
    },
    {
      target: '[data-tour="genie-artifact-send"]',
      content: 'onboarding.genieArtifact.send',
      placement: 'left',
    },
    {
      target: 'body',
      content: 'onboarding.genieArtifact.complete',
      placement: 'center',
    },
  ],
};

/**
 * Knowledge & AI Tour
 * Introduces the Knowledge section (conversations, agents, knowledge base) and
 * the per-org AI service settings. Anchored to the sidebar so it runs on any page.
 */
export const knowledgeTour: TourConfig = {
  id: 'knowledge',
  continuous: true,
  showProgress: true,
  showSkipButton: true,
  steps: [
    {
      target: 'body',
      content: 'onboarding.knowledge.welcome',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-knowledge"]',
      content: 'onboarding.knowledge.section',
      placement: 'right',
    },
    {
      target: '[data-tour="nav-conversations"]',
      content: 'onboarding.knowledge.conversations',
      placement: 'right',
    },
    {
      target: '[data-tour="nav-chatbots"]',
      content: 'onboarding.knowledge.chatbots',
      placement: 'right',
    },
    {
      target: '[data-tour="nav-knowledge-base"]',
      content: 'onboarding.knowledge.knowledgeBase',
      placement: 'right',
    },
    {
      target: '[data-tour="sidebar-organisation"]',
      content: 'onboarding.knowledge.serviceIA',
      placement: 'right',
    },
    {
      target: 'body',
      content: 'onboarding.knowledge.complete',
      placement: 'center',
    },
  ],
};

/**
 * All available tours in the application
 * Add new tours here as they are created for other pages/components
 */
export const tours: Record<string, TourConfig> = {
  welcome: welcomeTour,
  dashboard: dashboardTour,
  userJourney: userJourneyTour,
  genieArtifact: genieArtifactTour,
  genieImporter: genieImporterTour,
  knowledge: knowledgeTour,
};

/**
 * Get a tour by its ID
 */
export const getTour = (tourId: string): TourConfig | undefined => {
  return tours[tourId];
};

/**
 * Path the user must be on for a given tour step. When a tour is active, we redirect to this path.
 * Returns null for "transition" steps (e.g. step 6 and USER_JOURNEY_TO_CADRAGE_STEP in user journey) where we don't force a page.
 */
export function getRequiredPathForStep(tourId: string, stepIndex: number): string | null {
  if (tourId === 'dashboard') return '/dashboard';
  if (tourId === 'genieImporter') return '/ai-extraction';
  if (tourId === 'genieArtifact') return '/welcome';
  // welcome & knowledge are anchored to always-present chrome (sidebar/header):
  // no forced page so they run wherever the user already is.
  if (tourId === 'welcome' || tourId === 'knowledge') return null;
  if (tourId === 'userJourney') {
    if (stepIndex <= 5) return '/domains/new';
    if (stepIndex === 6) return null;  // transition to use case
    if (stepIndex < USER_JOURNEY_TO_CADRAGE_STEP) return '/cas-usage/new';
    if (stepIndex === USER_JOURNEY_TO_CADRAGE_STEP) return null; // transition to cadrage
    return '/cadrages/new';
  }
  return null;
}

/**
 * Paths that are allowed during the current tour step (for enabling nav links).
 * When a tour is active, only links to these paths (or matching prefixes) are clickable.
 */
export function getTourAllowedPaths(tourId: string, stepIndex: number): string[] {
  if (tourId === 'dashboard') return ['/dashboard'];
  if (tourId === 'genieImporter') return ['/ai-extraction'];
  if (tourId === 'genieArtifact') return ['/welcome'];
  // welcome & knowledge: no path restriction (sidebar-anchored, nav stays clickable).
  if (tourId === 'userJourney') {
    if (stepIndex <= 5) return ['/domains/new'];
    if (stepIndex === 6) return ['/domains', '/domains/new']; // transition: allow domains list/new/detail
    if (stepIndex < USER_JOURNEY_TO_CADRAGE_STEP) return ['/cas-usage/new'];
    if (stepIndex === USER_JOURNEY_TO_CADRAGE_STEP) return ['/cas-usage', '/cas-usage/new']; // transition
    return ['/cadrages/new'];
  }
  return [];
}

/** True if path is allowed during the current tour step (path can be exact or under an allowed prefix). */
export function isPathAllowedInTour(path: string, tourId: string, stepIndex: number): boolean {
  const allowed = getTourAllowedPaths(tourId, stepIndex);
  if (allowed.length === 0) return true; // no tour or unknown tour
  return allowed.some(
    (a) => path === a || path.startsWith(a + '/')
  );
}

/**
 * Hook that runs when AppLayout loads: checks if a tour is active and handles redirects,
 * auto-continue (e.g. domain page → use case page), and the "redirected" toast.
 * Call this once in AppLayout and use the returned state/actions for the tour UI.
 */
export function useTourGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeTourId, stepIndex, setStep, skipTour, requestResume } = useTourStore();
  const redirectToastShownRef = useRef(false);

  useEffect(() => {
    if (!activeTourId) {
      redirectToastShownRef.current = false;
      return;
    }
    const pathname = location.pathname;

    // After creating a domain we land on /domains/:id; advance to step 6 then auto-continue to use case page
    if (
      activeTourId === 'userJourney' &&
      stepIndex === 5 &&
      (pathname === '/domains' || pathname.startsWith('/domains/'))
    ) {
      setStep(6);
      return;
    }
    // Auto-continue: when on transition step 6 (domain page), go straight to use case creation
    if (
      activeTourId === 'userJourney' &&
      stepIndex === 6 &&
      (pathname === '/domains' || pathname.startsWith('/domains/'))
    ) {
      navigate('/cas-usage/new');
      setStep(7);
      return;
    }
    // Auto-continue: when on the cadrage transition step (cas-usage page), go straight to cadrage creation
    if (
      activeTourId === 'userJourney' &&
      stepIndex === USER_JOURNEY_TO_CADRAGE_STEP &&
      (pathname === '/cas-usage' || pathname.startsWith('/cas-usage/'))
    ) {
      navigate('/cadrages/new');
      setStep(USER_JOURNEY_TO_CADRAGE_STEP + 1);
      return;
    }
    const requiredPath = getRequiredPathForStep(activeTourId, stepIndex);
    if (requiredPath != null && pathname !== requiredPath) {
      if (!redirectToastShownRef.current) {
        notify.info(t('onboarding.redirectedToTour'), t('onboarding.redirectedToTourTitle'));
        redirectToastShownRef.current = true;
      }
      navigate(requiredPath);
    } else {
      redirectToastShownRef.current = false;
    }
  }, [activeTourId, stepIndex, location.pathname, navigate, setStep, t]);

  return { activeTourId, stepIndex, setStep, skipTour, requestResume };
}
